/**
 * RitualsDashboardCard - Dashboard widget for daily rituals
 * Shows today's ritual status and quick access to morning/evening flows
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { 
  Sunrise, Moon, Play, CheckCircle2, 
  Sparkles, ArrowRight, Target, Flame, ChevronRight
} from 'lucide-react';
import { RitualRoutineFlow } from './RitualRoutineFlow';
import { TomorrowActionsCard } from './TomorrowActionsCard';
import { RitualCelebration, isMilestoneStreak } from './RitualCelebration';
import { RoutineType } from './types';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { 
  useTodayRitualStatus, 
  useRitualStreak, 
  useYesterdayActions,
  useLogRitual,
  useReviewActions
} from '@/hooks/useRitualData';

interface RitualsDashboardCardProps {
  className?: string;
}

export const RitualsDashboardCard: React.FC<RitualsDashboardCardProps> = ({ className }) => {
  const navigate = useNavigate();
  const [showFlow, setShowFlow] = useState<RoutineType | null>(null);
  const [showTomorrowReview, setShowTomorrowReview] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationType, setCelebrationType] = useState<'completion' | 'streak_milestone' | 'both_complete'>('completion');
  const { toast } = useToast();

  // Use hooks for data
  const { data: todayStatus } = useTodayRitualStatus();
  const { data: streakData } = useRitualStreak();
  const { data: yesterdayActions } = useYesterdayActions();
  const logRitual = useLogRitual();
  const reviewActions = useReviewActions();

  const morningCompleted = todayStatus?.morning ?? false;
  const eveningCompleted = todayStatus?.evening ?? false;
  const streak = streakData?.currentStreak ?? 0;

  const hour = new Date().getHours();
  const isEvening = hour >= 17 || hour < 5;
  const suggestedRoutine: RoutineType = isEvening ? 'evening' : 'morning';

  const handleRoutineComplete = async (data: {
    items_completed: string[];
    total_duration_minutes: number;
    mood_before: number;
    mood_after: number;
    tomorrow_actions?: any;
  }) => {
    try {
      await logRitual.mutateAsync({
        routineType: showFlow!,
        moodBefore: data.mood_before,
        moodAfter: data.mood_after,
        activitiesCompleted: data.items_completed,
        tomorrowActions: data.tomorrow_actions ? {
          action1: data.tomorrow_actions.actions?.[0] || '',
          action2: data.tomorrow_actions.actions?.[1] || '',
          action3: data.tomorrow_actions.actions?.[2] || '',
          futureConnection: data.tomorrow_actions.futureConnection,
        } : undefined,
      });

      const completedRoutine = showFlow;
      setShowFlow(null);
      
      // Trigger celebration
      const newStreak = streak + 1;
      const willBeBothComplete = 
        (completedRoutine === 'morning' && eveningCompleted) ||
        (completedRoutine === 'evening' && morningCompleted);

      if (willBeBothComplete) {
        setCelebrationType('both_complete');
      } else if (isMilestoneStreak(newStreak)) {
        setCelebrationType('streak_milestone');
      } else {
        setCelebrationType('completion');
      }
      setShowCelebration(true);
      
      toast({
        title: `${completedRoutine === 'morning' ? 'Morning' : 'Evening'} habit complete!`,
        description: data.mood_after > data.mood_before 
          ? `Your mood improved by ${data.mood_after - data.mood_before} points`
          : "Well done for showing up for yourself.",
      });
    } catch (error) {
      console.error('Error saving habit:', error);
      toast({
        title: "Error saving habit",
        description: "Your progress was tracked but couldn't be saved.",
        variant: "destructive",
      });
    }
  };

  const handleTomorrowReviewSave = async (data: any) => {
    if (!yesterdayActions) return;

    try {
      await reviewActions.mutateAsync({
        actionId: yesterdayActions.id,
        action1Completed: data.completed?.[0] ?? false,
        action2Completed: data.completed?.[1] ?? false,
        action3Completed: data.completed?.[2] ?? false,
        barriers: data.barriers,
      });

      setShowTomorrowReview(false);
      toast({
        title: "Review saved",
        description: "Great reflection on yesterday's actions!",
      });
    } catch (error) {
      console.error('Error saving review:', error);
    }
  };

  return (
    <>
      <Card className={cn("border-border/50 overflow-hidden", className)}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-secondary/30 to-primary/30 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base font-medium">Daily Healthy Habits</CardTitle>
                <p className="text-xs text-muted-foreground">Morning & evening routines</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {streak > 0 && (
                <Badge variant="secondary" className="gap-1">
                  <Flame className="w-3 h-3 text-secondary" />
                  {streak}
                </Badge>
              )}
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => navigate('/rituals')}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Progress Overview */}
          <div className="flex gap-3">
            {/* Morning */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => !morningCompleted && setShowFlow('morning')}
              className={cn(
                "flex-1 p-3 rounded-xl border transition-all text-left",
                morningCompleted 
                  ? "bg-secondary/10 border-secondary/30" 
                  : "bg-gradient-to-br from-secondary/5 to-background border-secondary/20 hover:border-secondary/40"
              )}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center",
                  morningCompleted ? "bg-secondary/20" : "bg-secondary/10"
                )}>
                  {morningCompleted ? (
                    <CheckCircle2 className="w-4 h-4 text-secondary" />
                  ) : (
                    <Sunrise className="w-4 h-4 text-secondary" />
                  )}
                </div>
                <span className="text-sm font-medium">Morning</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {morningCompleted ? "Completed ✓" : "~20 min routine"}
              </p>
            </motion.button>

            {/* Evening */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => !eveningCompleted && setShowFlow('evening')}
              className={cn(
                "flex-1 p-3 rounded-xl border transition-all text-left",
                eveningCompleted 
                  ? "bg-primary/10 border-primary/30" 
                  : "bg-gradient-to-br from-primary/5 to-background border-primary/20 hover:border-primary/40"
              )}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center",
                  eveningCompleted ? "bg-primary/20" : "bg-primary/10"
                )}>
                  {eveningCompleted ? (
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                  ) : (
                    <Moon className="w-4 h-4 text-primary" />
                  )}
                </div>
                <span className="text-sm font-medium">Evening</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {eveningCompleted ? "Completed ✓" : "~25 min routine"}
              </p>
            </motion.button>
          </div>

          {/* Yesterday's Actions Review (if applicable) */}
          {yesterdayActions && !morningCompleted && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTomorrowReview(true)}
              className="w-full gap-2 border-accent/30 text-accent hover:bg-accent/10"
            >
              <Target className="w-4 h-4" />
              Review yesterday's 3 actions
            </Button>
          )}

          {/* Quick Start CTA */}
          {!morningCompleted && !eveningCompleted && (
            <Button 
              onClick={() => setShowFlow(suggestedRoutine)}
              className="w-full gap-2"
            >
              <Play className="w-4 h-4" />
              Start {suggestedRoutine === 'morning' ? 'Morning' : 'Evening'} Habit
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}

          {/* Both complete message */}
          {morningCompleted && eveningCompleted && (
            <div className="text-center py-2">
              <Badge className="bg-primary/20 text-primary border-0">
                <Sparkles className="w-3 h-3 mr-1" />
                All habits complete today!
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ritual Flow Dialog */}
      <Dialog open={showFlow !== null} onOpenChange={() => setShowFlow(null)}>
        <DialogContent className="max-w-lg p-0 border-0 bg-transparent">
          {showFlow && (
            <RitualRoutineFlow
              routineType={showFlow}
              onComplete={handleRoutineComplete}
              onClose={() => setShowFlow(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Tomorrow Actions Review Dialog */}
      <Dialog open={showTomorrowReview} onOpenChange={setShowTomorrowReview}>
        <DialogContent className="max-w-lg">
          {yesterdayActions && (
            <TomorrowActionsCard
              mode="review"
              actions={{
                action_1: yesterdayActions.action_1,
                action_2: yesterdayActions.action_2,
                action_3: yesterdayActions.action_3,
                action_1_completed: yesterdayActions.action_1_completed,
                action_2_completed: yesterdayActions.action_2_completed,
                action_3_completed: yesterdayActions.action_3_completed,
              }}
              onSave={handleTomorrowReviewSave}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Celebration Overlay */}
      <RitualCelebration
        show={showCelebration}
        type={celebrationType}
        streakCount={streak + 1}
        routineType={showFlow || undefined}
        onComplete={() => setShowCelebration(false)}
      />
    </>
  );
};
